package org.example.project.screens_shared

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.dashboard.ScreenManager
import org.example.project.dashboard.DashboardNode
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.LizenzService
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.RollenService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import kotlin.math.ceil

private const val ROLLEN_ROW_GRID = "170px 120px"
private const val ROLLEN_MIN_COL_W = 320
private const val ROLLEN_BLOCK_GAP = 32
private const val ROLLEN_HEADER_HEIGHT = 34
private const val ROLLEN_ROW_HEIGHT = 53

private val rollenJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun rollenApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadRollen(): List<All> {
    val userId = org.example.project.service.LoginService.getId()
    val firmenId = org.example.project.service.LoginService.getUser()?.firmenId.orEmpty().trim()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("rollenverwaltung/user/${rollenApiPart(userId)}/${rollenApiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { rollenJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun createRolle(role: All): All? {
    val userId = org.example.project.service.LoginService.getId()
    val firmenId = org.example.project.service.LoginService.getUser()?.firmenId.orEmpty().trim()
    if (userId.isBlank()) return null
    val response = runCatching {
        window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/rollenverwaltung/create-json",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = rollenJson.encodeToString(
                    buildJsonObject {
                        put("userId", userId)
                        put("firmenId", firmenId)
                        put("role", rollenJson.encodeToJsonElement(All.AllNames.serializer(), role))
                    }
                )
            )
        ).await()
    }.getOrNull() ?: return null
    if (!response.ok) return null
    val raw = response.text().await()
    if (raw.isBlank() || raw == "null") return null
    return runCatching { rollenJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun saveRolle(role: All): All? {
    val userId = org.example.project.service.LoginService.getId()
    val firmenId = org.example.project.service.LoginService.getUser()?.firmenId.orEmpty().trim()
    if (userId.isBlank() || role.id.isBlank()) return null
    val response = runCatching {
        window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/rollenverwaltung/save-json",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = rollenJson.encodeToString(
                    buildJsonObject {
                        put("userId", userId)
                        put("firmenId", firmenId)
                        put("role", rollenJson.encodeToJsonElement(All.AllNames.serializer(), role))
                    }
                )
            )
        ).await()
    }.getOrNull() ?: return null
    if (!response.ok) return null
    val raw = response.text().await()
    if (raw.isBlank() || raw == "null") return null
    return runCatching { rollenJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun deleteRolle(roleId: String) {
    val userId = org.example.project.service.LoginService.getId()
    val firmenId = org.example.project.service.LoginService.getUser()?.firmenId.orEmpty().trim()
    if (userId.isBlank() || roleId.isBlank()) return
    LumaticaApiManager.call(
        "rollenverwaltung/delete/${rollenApiPart(userId)}/${rollenApiPart(roleId)}/${rollenApiPart(firmenId)}"
    )
}

private val BERECHTIGUNG_OPTIONS = listOf(
    All(key = RollenService.KEINE, label = "Deaktiviert"),
    All(key = RollenService.LESEN, label = "Lesen"),
    All(key = RollenService.SCHREIBEN, label = "Schreiben"),
    All(key = RollenService.ALLES, label = "Vollzugriff")
)

private val PERSONAL_CHILD_SCREENS = setOf(
    "Arbeitszeitverwaltung",
    "Buchungsjournal",
    "Personalverwaltung",
    "MitarbeiterKalender",
    "MitarbeiterMeinUrlaub",
    "MitarbeiterZeitkonto",
    "Urlaubsuebersicht",
    "Zeitverwaltung"
)

private fun permissionForNewScreen(role: All, screenKey: String): String {
    if (screenKey == "LinkedInChat") {
        role.screenBerechtigungen["Vertriebsindex"]?.let { return it }
    }
    if (screenKey == "SYSVertriebsDashboard") {
        role.screenBerechtigungen["VertriebsDashboard"]?.let { return it }
    }
    if (screenKey == "AufgabenViewer") {
        role.screenBerechtigungen["MitarbeiterKalender"]?.let { return it }
    }
    if (screenKey in PERSONAL_CHILD_SCREENS) {
        role.screenBerechtigungen["Arbeitszeitverwaltung"]?.let { return it }
    }
    val assigned = role.screenBerechtigungen.values.filter { it != RollenService.KEINE }
    val uniform = assigned.distinct().singleOrNull()
    return uniform ?: RollenService.KEINE
}

private fun rolleWithCurrentScreens(role: All, screenKeys: List<String>): All {
    val missing = screenKeys.filterNot { it in role.screenBerechtigungen }
    if (missing.isEmpty()) return role
    return role.copy(
        screenBerechtigungen = role.screenBerechtigungen + missing.associateWith { permissionForNewScreen(role, it) }
    )
}

@Composable
fun SHAREDRollenverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    var rollen by remember { mutableStateOf(emptyList<All>()) }
    var selectedRolleId by remember { mutableStateOf("") }
    var draftRolle by remember { mutableStateOf<All?>(null) }
    var numCols by remember { mutableStateOf(1) }
    var containerEl by remember { mutableStateOf<org.w3c.dom.Element?>(null) }
    val effectiveNode = remember(art) {
        when (art) {
            NodeScope.CUS -> DashboardNode.CUS
            NodeScope.NET -> DashboardNode.NET
            else -> DashboardNode.SYS
        }
    }
    val allScreenKeys = remember(effectiveNode) {
        when (effectiveNode) {
            DashboardNode.CUS -> ScreenManager.roleScreenKeys(effectiveNode)
            DashboardNode.SYS, DashboardNode.NET -> ScreenManager.roleScreenKeys(effectiveNode)
            else -> emptyList()
        }.distinct().sorted()
    }
    val sortedScreenKeys = remember(allScreenKeys) { allScreenKeys.sortedBy { ScreenManager.labelForKey(it).lowercase() } }

    fun measure() {
        val rect = containerEl?.getBoundingClientRect() ?: return
        val w = rect.width
        val h = rect.height
        if (w <= 0 || h <= 0) return

        val maxColsByWidth = ((w + ROLLEN_BLOCK_GAP) / (ROLLEN_MIN_COL_W + ROLLEN_BLOCK_GAP))
            .toInt()
            .coerceAtLeast(1)
        val usableHeight = (h - ROLLEN_HEADER_HEIGHT).coerceAtLeast(ROLLEN_ROW_HEIGHT.toDouble())
        val maxRowsPerColumn = (usableHeight / ROLLEN_ROW_HEIGHT).toInt().coerceAtLeast(1)
        val colsNeededByHeight = ceil(sortedScreenKeys.size / maxRowsPerColumn.toDouble()).toInt().coerceAtLeast(1)

        numCols = colsNeededByHeight
            .coerceAtMost(maxColsByWidth)
            .coerceIn(1, 8)
    }

    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = { measure() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    suspend fun reloadRoles() {
        val loaded = loadRollen()
        val normalized = loaded.map { rolleWithCurrentScreens(it, allScreenKeys) }
        normalized.zip(loaded).forEach { (next, old) ->
            if (next.screenBerechtigungen != old.screenBerechtigungen) {
                saveRolle(next)
            }
        }
        rollen = normalized.sortedBy { it.name.ifBlank { it.id }.lowercase() }
        if (rollen.none { it.id == selectedRolleId }) {
            selectedRolleId = rollen.firstOrNull()?.id.orEmpty()
        }
        draftRolle = rollen.firstOrNull { it.id == selectedRolleId }
    }

    suspend fun refreshCurrentRole() {
        RollenService.refreshCurrentUserRole(art)
    }

    LaunchedEffect(art) {
        reloadRoles()
    }

    LaunchedEffect(selectedRolleId) {
        draftRolle = rollen.firstOrNull { it.id == selectedRolleId }
    }

    fun createRole() {
        org.example.project.dashboard.AppDialogs.prompt("Rollenname:") { input ->
            val name = input?.takeIf { it.isNotBlank() } ?: return@prompt
            val emptyPermissions = allScreenKeys.distinct().associateWith { RollenService.KEINE }
            val role = All(name = name, screenBerechtigungen = emptyPermissions)
            scope.launch {
                val savedRole = createRolle(role) ?: return@launch
                reloadRoles()
                refreshCurrentRole()
                selectedRolleId = savedRole.id
            }
        }
    }

    fun deleteRole() {
        val roleId = selectedRolleId.takeIf { it.isNotBlank() } ?: return
        scope.launch {
            deleteRolle(roleId)
            reloadRoles()
            refreshCurrentRole()
        }
    }

    fun setPermission(screenKey: String, value: String) {
        draftRolle = draftRolle?.let { role ->
            role.copy(screenBerechtigungen = role.screenBerechtigungen + (screenKey to value))
        }
    }

    fun setAllPermissions(value: String) {
        val keys = sortedScreenKeys
        if (keys.isEmpty()) return
        draftRolle = draftRolle?.let { role ->
            role.copy(screenBerechtigungen = keys.associateWith { value })
        }
    }

    val columns = if (sortedScreenKeys.isEmpty() || numCols == 0) listOf(emptyList())
    else sortedScreenKeys.chunked(ceil(sortedScreenKeys.size / numCols.toDouble()).toInt())

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    flex(1)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    property("flex-direction", "column")
                }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Rollenverwaltung") }
                    }
                }

                Div({
                    classes(SharedWorkspaceStyle.actionBar)
                    style { maxWidth(100.percent) }
                }) {
                    AppDropdown(
                        options = rollen.map { All(key = it.id, label = it.name.ifBlank { it.id }) },
                        selectedValue = selectedRolleId.ifBlank { null },
                        onChange = { selectedRolleId = it },
                        placeholder = "Rolle wählen",
                        appearance = AppDropdownAppearance.Field,
                        menuMode = AppDropdownMenuMode.Overlay,
                        attrs = { style { width(220.px); height(42.px) } }
                    )
                    LumaticaButton(
                        label = "Erstellen",
                        onClick = { createRole() },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Löschen",
                        onClick = { deleteRole() },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = selectedRolleId.isNotBlank(),
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Lesen",
                        onClick = { setAllPermissions(RollenService.LESEN) },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = draftRolle != null,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Schreiben",
                        onClick = { setAllPermissions(RollenService.SCHREIBEN) },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = draftRolle != null,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Vollzugriff",
                        onClick = { setAllPermissions(RollenService.ALLES) },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = draftRolle != null,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            val role = draftRolle ?: return@LumaticaButton
                            scope.launch {
                                saveRolle(rolleWithCurrentScreens(role, allScreenKeys))
                                reloadRoles()
                                refreshCurrentRole()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = draftRolle != null,
                        heightPx = 42
                    )
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                if (draftRolle == null) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
                            color(org.jetbrains.compose.web.css.Color.white)
                            fontSize(14.px)
                            padding(40.px)
                        }
                    }) { Text("Keine Rolle ausgewählt.") }
                } else {
                    Div({
                        classes(SharedWorkspaceStyle.columnList)
                        style {
                            flex(1)
                            minHeight(0.px)
                            overflowY("auto")
                        }
                        ref { el ->
                            containerEl = el
                            window.requestAnimationFrame { measure() }
                            onDispose { containerEl = null }
                        }
                    }) {
                        Div({
                            classes(SharedWorkspaceStyle.permissionColumns)
                            style {
                                property("grid-template-columns", "repeat($numCols, ${ROLLEN_MIN_COL_W}px)")
                            }
                        }) {
                            columns.forEachIndexed { index, columnKeys ->
                                Div({
                                    classes(SharedWorkspaceStyle.permissionColumn)
                                    style {
                                        minWidth(0.px)
                                        width(100.percent)
                                        if (index < columns.lastIndex) {
                                            property("border-right", "1px solid rgba(255,255,255,0.07)")
                                            property("padding-right", "20px")
                                        }
                                    }
                                }) {
                                    Div({
                                        classes(SharedWorkspaceStyle.permissionHeader)
                                        style { property("grid-template-columns", ROLLEN_ROW_GRID) }
                                    }) {
                                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text("Screen") }
                                        Span({ classes(SharedWorkspaceStyle.centeredHeader) }) { Text("Berechtigung") }
                                    }
                                    columnKeys.forEach { screenKey ->
                                        val permission = draftRolle?.screenBerechtigungen?.get(screenKey) ?: RollenService.KEINE
                                        Div({
                                            classes(SharedWorkspaceStyle.permissionRow)
                                            style { property("grid-template-columns", ROLLEN_ROW_GRID) }
                                        }) {
                                            Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(ScreenManager.labelForKey(screenKey)) }
                                            AppDropdown(
                                                options = BERECHTIGUNG_OPTIONS,
                                                selectedValue = permission,
                                                onChange = { setPermission(screenKey, it) },
                                                appearance = AppDropdownAppearance.Underline,
                                                menuMode = AppDropdownMenuMode.Overlay,
                                                attrs = {
                                                    classes(SharedWorkspaceStyle.permissionSelect)
                                                    style { width(120.px) }
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
